import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Wand2, FileSearch, Download } from 'lucide-react';

const exportJson = (report) => {
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `pre-canonical-report-${(report.finished_at || '').slice(0, 19).replace(/:/g, '')}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/admin/llm-dedup/pre-canonical`;
const MODE_LABELS = { off: 'Off', shadow: 'Shadow', live: 'Live' };

const Name = ({ v }) => (
  <span>{v ? `${v.artist} — ${v.title}` : '—'}</span>
);

const VariantCell = ({ info, testId }) => (
  <td className="p-2 align-top" data-testid={testId}>
    <div className="text-gray-500"><Name v={info.before} /></div>
    {info.eligible ? (
      <div className={info.changed ? 'text-emerald-700 dark:text-emerald-300 font-medium' : 'text-gray-400'}>
        → <Name v={info.after} />
      </div>
    ) : (
      <div className="text-amber-700 dark:text-amber-300">παράκαμψη: {info.why}</div>
    )}
    <div className="text-[10px] text-gray-400 mt-0.5">
      {(info.stations || []).slice(0, 4).map((s) => `${s.name || s.id.slice(-6)} (${s.provider})`).join(', ') || 'κανένας σταθμός'}
    </div>
  </td>
);

const ReportTable = ({ report }) => (
  <div className="space-y-2" data-testid="pre-canonical-report">
    <div className="flex flex-wrap gap-3 text-xs" data-testid="pre-canonical-summary">
      {Object.entries(report.summary).map(([k, v]) => (
        <span key={k} className="px-2 py-1 rounded bg-white dark:bg-gray-900"><b>{k}:</b> {v}</span>
      ))}
    </div>
    <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-gray-100 dark:bg-gray-800">
          <tr><th className="p-2 text-left">Εκδοχή A</th><th className="p-2 text-left">Εκδοχή B</th><th className="p-2">Ίδιες πριν</th><th className="p-2">Ίδιες μετά</th><th className="p-2">Φωνητικά ίδιες</th></tr>
        </thead>
        <tbody>
          {report.rows.map((r, i) => (
            <tr key={r.dedup_key} className="border-t border-gray-200 dark:border-gray-700" data-testid={`pre-canonical-row-${i}`}>
              <VariantCell info={r.variant_a} testId={`pre-canonical-row-${i}-a`} />
              <VariantCell info={r.variant_b} testId={`pre-canonical-row-${i}-b`} />
              <td className="p-2 text-center">{r.identical_before ? '✓' : '—'}</td>
              <td className="p-2 text-center font-bold">{r.identical_after ? '✓' : '—'}</td>
              <td className="p-2 text-center" data-testid={`pre-canonical-row-${i}-phonetic`}>{r.phonetic_identical ? '✓' : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const PreCanonicalPanel = ({ authConfig }) => {
  const [mode, setMode] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState(null);

  const loadMode = useCallback(async () => {
    try { setMode((await axios.get(`${BASE}/mode`, authConfig)).data.mode); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  }, [authConfig]);

  useEffect(() => { loadMode(); }, [loadMode]);

  const changeMode = async (m) => {
    if (m === 'live' && !window.confirm('LIVE: οι νέες συγχωνεύσεις θα επαληθεύονται με τα κανονικοποιημένα ονόματα. Συνέχεια;')) return;
    setErr('');
    try { setMode((await axios.put(`${BASE}/mode?mode=${m}`, {}, authConfig)).data.mode); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  };

  const mounted = useRef(true);
  const timer = useRef(null);
  const schedule = (fn, ms) => { clearTimeout(timer.current); timer.current = setTimeout(fn, ms); };

  const poll = useCallback(async () => {
    try {
      const d = (await axios.get(`${BASE}/shadow-report`, authConfig)).data;
      if (!mounted.current) return;
      setStatus(d);
      if (d.status === 'done') { setReport(d); setBusy(false); }
      else if (d.status === 'running') { setBusy(true); schedule(poll, 4000); }
      else { setBusy(false); if (d.status === 'error') setErr(d.error); }
    } catch (e) {
      if (!mounted.current) return;
      setBusy(false); setErr(e.response?.data?.detail || e.message);
    }
  }, [authConfig]);

  useEffect(() => {
    mounted.current = true;
    poll();
    return () => { mounted.current = false; clearTimeout(timer.current); };
  }, [poll]);

  const loadReport = async () => {
    setBusy(true); setErr('');
    try { await axios.post(`${BASE}/shadow-report?limit=100`, {}, authConfig); schedule(poll, 1500); }
    catch (e) { setBusy(false); setErr(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-800 space-y-3" data-testid="pre-canonical-panel">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="flex items-center gap-1 font-semibold"><Wand2 className="w-4 h-4 text-purple-600" />Προ-κανονικοποίηση (μόνο σταθμοί metadata):</span>
        <div className="flex rounded overflow-hidden border border-gray-300 dark:border-gray-600" data-testid="pre-canonical-mode-toggle">
          {Object.keys(MODE_LABELS).map((m) => (
            <button key={m} onClick={() => changeMode(m)} data-testid={`pre-canonical-mode-${m}`}
              className={`px-3 py-1 transition-colors ${mode === m ? 'bg-purple-600 text-white' : 'bg-white dark:bg-gray-900 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>
        <button onClick={loadReport} disabled={busy} data-testid="pre-canonical-report-btn"
          className="px-3 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 flex items-center gap-1 disabled:opacity-50">
          <FileSearch className={`w-4 h-4 ${busy ? 'animate-pulse' : ''}`} />
          {busy ? `Υπολογισμός... ${status?.phase || ''} ${status?.progress || ''}` : 'Αναφορά «τι θα άλλαζε»'}
        </button>
        {report?.finished_at && !busy && (
          <button onClick={() => exportJson(report)} data-testid="pre-canonical-export-btn"
            className="px-3 py-1 rounded bg-blue-100 hover:bg-blue-200 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200 flex items-center gap-1">
            <Download className="w-4 h-4" />Εξαγωγή JSON αναφοράς
          </button>
        )}
        {report?.finished_at && !busy && (
          <span className="text-gray-500" data-testid="pre-canonical-report-meta">
            τελευταία: {new Date(report.finished_at).toLocaleString('el-GR')} ({report.seconds}s)
          </span>
        )}
        {err && <span className="text-red-600" data-testid="pre-canonical-error">{String(err)}</span>}
      </div>
      {report && <ReportTable report={report} />}
    </div>
  );
};
